import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getExtensionPropertyBoolean } from '../../utilities/extension-property.util';
import { IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';

export interface IInventoryStatus {
    type: 'to-order' | 'central-warehouse' | 'in-store' | 'out-of-stock';
    canAddToCart: boolean;
    storeNames?: string[];
}

/**
 * Central warehouse ID constant
 */
const CENTRAL_WAREHOUSE_ID = '1001';

/**
 * Determines the inventory status for the product
 * @param product - The product to check
 * @param productAvailableQuantity - Array of inventory information from different locations
 * @returns The inventory status with type, cart availability, and optional store names
 */
export function getInventoryStatus(
    product: SimpleProduct | undefined,
    productAvailableQuantity: IProductInventoryInformation[] | undefined
): IInventoryStatus {
    if (!product) {
        return { type: 'out-of-stock', canAddToCart: false };
    }

    // Check if this is a to-order item
    const isToOrder = getExtensionPropertyBoolean(product.ExtensionProperties, 'GHA_ToOrder', false);

    if (isToOrder) {
        return { type: 'to-order', canAddToCart: true };
    }

    // Check for central warehouse inventory
    const hasCentralWarehouseInventory = checkCentralWarehouseInventory(productAvailableQuantity);

    if (hasCentralWarehouseInventory) {
        return { type: 'central-warehouse', canAddToCart: true };
    }

    // Check for store inventory
    const storeNames = getStoresWithInventory(productAvailableQuantity);

    if (storeNames && storeNames.length > 0) {
        return { type: 'in-store', canAddToCart: true, storeNames };
    }

    // No inventory anywhere
    return { type: 'out-of-stock', canAddToCart: false };
}

/**
 * Checks if there is inventory in the central warehouse (1001)
 * @param productAvailableQuantity - Array of inventory information
 * @returns True if central warehouse has available inventory
 */
export function getCentralWarehouseInventory(productAvailableQuantity: IProductInventoryInformation[] | undefined): number {
    if (!productAvailableQuantity || productAvailableQuantity.length === 0) {
        return 0;
    }

    // Find the central warehouse inventory record and return the available quantity
    const centralWarehouse = productAvailableQuantity.find(inventory => {
        const inventLocationId = inventory.InventLocationId;
        return inventLocationId === CENTRAL_WAREHOUSE_ID;
    });

    if (!centralWarehouse) {
        return 0;
    }

    const isAvailable = centralWarehouse.IsProductAvailable ?? false;
    const availableQty = centralWarehouse.ProductAvailableQuantity?.AvailableQuantity ?? 0;

    return isAvailable && availableQty > 0 ? availableQty : 0;
}

/**
 * Checks if there is inventory in the central warehouse (1001)
 * @param productAvailableQuantity - Array of inventory information
 * @returns True if central warehouse has available inventory
 */
function checkCentralWarehouseInventory(productAvailableQuantity: IProductInventoryInformation[] | undefined): boolean {
    if (!productAvailableQuantity || productAvailableQuantity.length === 0) {
        return false;
    }

    // Check if any of the inventory records is for central warehouse with available quantity
    return productAvailableQuantity.some(inventory => {
        const isAvailable = inventory.IsProductAvailable ?? false;
        const availableQty = inventory.ProductAvailableQuantity?.AvailableQuantity ?? 0;
        const inventLocationId = inventory.InventLocationId;

        return inventLocationId === CENTRAL_WAREHOUSE_ID && isAvailable && availableQty > 0;
    });
}

/**
 * Gets list of store names that have inventory (excluding central warehouse)
 * @param productAvailableQuantity - Array of inventory information
 * @returns Array of store location IDs with inventory, or undefined if none found
 */
function getStoresWithInventory(productAvailableQuantity: IProductInventoryInformation[] | undefined): string[] | undefined {
    if (!productAvailableQuantity || productAvailableQuantity.length === 0) {
        return undefined;
    }

    const storeNames: string[] = [];

    for (const inventory of productAvailableQuantity) {
        const isAvailable = inventory.IsProductAvailable ?? false;
        const availableQty = inventory.ProductAvailableQuantity?.AvailableQuantity ?? 0;
        const inventLocationId = inventory.InventLocationId;

        // Skip central warehouse
        if (inventLocationId === CENTRAL_WAREHOUSE_ID) {
            continue;
        }

        // If it's a store with inventory, add it
        if (inventLocationId && isAvailable && availableQty > 0) {
            storeNames.push(inventLocationId);
        }
    }

    return storeNames.length > 0 ? storeNames : undefined;
}
